import React, {FC} from 'react';
import {styled} from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelExternalTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelBehaviorsModel';
import {ChannelTypesEnum} from '../../../../../libs/shared/core/src/models/api/channelCommonTypes';
import {
  ChannelIcon,
  ChannelIconSizesEnum,
  ChannelIconVisualTypesEnum,
} from '../../../../../libs/shared/ui/src/components/icon/channelIcon';
import {onboardingGuideRightSideWidth, onboardingGuideSlideHeight} from '../../guide/onboardingGuideHelpers';
import {
  StyledVisualContainer,
  StyledVisualDetailsText,
  StyledVisualLayout,
} from './onboardingBasicsGuideHelpers';

/*
 * Props.
 */

interface OnboardingBasicsGuideChannelsIntroVisualProps {}

/*
 * Style.
 */

const StyledChannelIcon = styled(ChannelIcon)`
  position: absolute;
  filter: drop-shadow(0px 0px 10px rgba(0, 0, 0, 0.1));
`;

const StyledIconsContainer = styled.div`
  position: absolute;
  width: ${onboardingGuideRightSideWidth};
  height: ${onboardingGuideSlideHeight};
  overflow: hidden;

  animation-name: fadeInIcons;
  animation-delay: 700ms;
  animation-duration: 400ms;
  animation-fill-mode: forwards;

  opacity: 0;

  @keyframes fadeInIcons {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
`;

const StyledFacebookChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(247, 57)}px;
  left: ${computeOffset(193, 57)}px;

  ${computeScaleAnimation('facebook', 57 / 40)}
`;

const StyledFrontChatChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(309, 45)}px;
  left: ${computeOffset(234, 45)}px;

  ${computeScaleAnimation('front-chat', 45 / 40)}
`;

const StyledWhatsappChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(254, 68)}px;
  left: ${computeOffset(296, 68)}px;

  ${computeScaleAnimation('whatsapp', 68 / 40)}
`;

const StyledTwilioChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(332, 45)}px;
  left: ${computeOffset(364, 45)}px;

  ${computeScaleAnimation('twilio', 45 / 40)}
`;

const StyledInstagramChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(346, 45)}px;
  left: ${computeOffset(303, 45)}px;

  ${computeScaleAnimation('instagram', 45 / 40)}
`;

const StyledMicrosoftChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(406, 68)}px;
  left: ${computeOffset(321, 68)}px;

  ${computeScaleAnimation('microsoft', 68 / 40)}
`;

const StyledTwitterChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(459, 57)}px;
  left: ${computeOffset(254, 57)}px;

  ${computeScaleAnimation('twitter', 57 / 40)}
`;

const StyledDialpadChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(433, 45)}px;
  left: ${computeOffset(163, 45)}px;

  ${computeScaleAnimation('dialpad', 45 / 40)}
`;

const StyledSlackChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(372, 68)}px;
  left: ${computeOffset(216, 68)}px;

  ${computeScaleAnimation('slack', 68 / 40)}
`;

const StyledGmailChannelIcon = styled(StyledChannelIcon)`
  top: ${computeOffset(327, 68)}px;
  left: ${computeOffset(132, 68)}px;

  ${computeScaleAnimation('gmail', 68 / 40)}
`;

const StyledChannelsIntrolDetailsText = styled(StyledVisualDetailsText)`
  animation-delay: 700ms;
  animation-duration: 400ms;
`;

/*
 * Component.
 */

export const OnboardingBasicsGuideChannelsIntroVisual: FC<
  OnboardingBasicsGuideChannelsIntroVisualProps
> = () => (
  <StyledVisualContainer>
    <StyledIconsContainer>
      <StyledFacebookChannelIcon type={ChannelTypesEnum.FACEBOOK} size={ChannelIconSizesEnum.BIG} />
      <StyledFrontChatChannelIcon type={ChannelTypesEnum.FRONT_CHAT} size={ChannelIconSizesEnum.BIG} />
      <StyledWhatsappChannelIcon type={ChannelExternalTypesEnum.WHATSAPP} size={ChannelIconSizesEnum.BIG} />
      <StyledTwilioChannelIcon type={ChannelTypesEnum.TWILIO} size={ChannelIconSizesEnum.BIG} />
      <StyledInstagramChannelIcon type={ChannelExternalTypesEnum.INSTAGRAM} size={ChannelIconSizesEnum.BIG} />
      <StyledMicrosoftChannelIcon
        type={ChannelIconVisualTypesEnum.MICROSOFT}
        size={ChannelIconSizesEnum.BIG}
      />
      <StyledTwitterChannelIcon type={ChannelTypesEnum.TWITTER} size={ChannelIconSizesEnum.BIG} />
      <StyledDialpadChannelIcon type={ChannelExternalTypesEnum.DIALPAD} size={ChannelIconSizesEnum.BIG} />
      <StyledSlackChannelIcon type={ChannelExternalTypesEnum.SLACK} size={ChannelIconSizesEnum.BIG} />
      <StyledGmailChannelIcon type={ChannelTypesEnum.GMAIL} size={ChannelIconSizesEnum.BIG} />
    </StyledIconsContainer>
    <StyledVisualLayout>
      <StyledChannelsIntrolDetailsText>
        <Trans id="onboarding-basics-guide-channels-intro-visual-details">
          A <span>channel</span> is the method through which customers contact you, like a specific email
          address or phone number. Email, chat, social media, SMS are just a few examples of the channels
          Front supports.
        </Trans>
      </StyledChannelsIntrolDetailsText>
    </StyledVisualLayout>
  </StyledVisualContainer>
);

function computeScaleAnimation(slug: string, scale: number) {
  const animationName = `scale-${slug}`;
  return `
    animation-name: ${animationName};
    animation-delay: 700ms;
    animation-duration: 400ms;
    animation-fill-mode: forwards;

    transform: scale(${scale * 0.5});

    @keyframes ${animationName} {
      0% {
        transform: scale(${scale * 0.5});
      }
      100% {
        transform: scale(${scale});
      }
    }
  `;
}

function computeOffset(position: number, scale: number) {
  return position + (scale - 40) / 2; // 40 because that's the icon's original size.
}
